import { ref } from 'vue'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: number
  type: ToastType
  message: string
}

const MAX_TOASTS = 5

const toasts = ref<Toast[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextId = 0

function add(type: ToastType, message: string, duration = 4000) {
  // Deduplicate: if same type+message exists, reset its timer
  const existing = toasts.value.find((t) => t.type === type && t.message === message)
  if (existing) {
    const oldTimer = timers.get(existing.id)
    if (oldTimer) clearTimeout(oldTimer)
    timers.set(
      existing.id,
      setTimeout(() => {
        toasts.value = toasts.value.filter((t) => t.id !== existing.id)
        timers.delete(existing.id)
      }, duration),
    )
    return
  }

  const id = nextId++
  toasts.value.push({ id, type, message })

  // Enforce max visible toasts — remove oldest when over limit
  while (toasts.value.length > MAX_TOASTS) {
    const removed = toasts.value.shift()!
    const timer = timers.get(removed.id)
    if (timer) clearTimeout(timer)
    timers.delete(removed.id)
  }

  timers.set(
    id,
    setTimeout(() => {
      toasts.value = toasts.value.filter((t) => t.id !== id)
      timers.delete(id)
    }, duration),
  )
}

function dismiss(id: number) {
  toasts.value = toasts.value.filter((t) => t.id !== id)
  const timer = timers.get(id)
  if (timer) clearTimeout(timer)
  timers.delete(id)
}

export function useToast() {
  return {
    toasts,
    success: (msg: string) => add('success', msg),
    error: (msg: string) => add('error', msg, 6000),
    info: (msg: string) => add('info', msg),
    warning: (msg: string) => add('warning', msg, 5000),
    dismiss,
  }
}
